import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useTheme } from "../lib/theme";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Languages, Sun, Moon } from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import PageHelp from "../components/PageHelp";

const DEMO_ACCOUNTS = [
  ["admin@nafelogistica.com", "admin123", "Admin"],
  ["operator@nafelogistica.com", "operator123", "Operador"],
  ["deliverer@nafelogistica.com", "deliverer123", "Entregador"],
  ["client@nafelogistica.com", "client123", "Cliente"],
];

function showDemoLogins() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host.includes("homolog");
}

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const demoLogins = showDemoLogins();
  const [email, setEmail] = useState(() => (demoLogins ? "admin@nafelogistica.com" : ""));
  const [password, setPassword] = useState(() => (demoLogins ? "admin123" : ""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error || t("invalid"));
  };

  return (
    <div className="min-h-screen bg-[var(--bg-main)] p-4 sm:p-6 flex items-center justify-center">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 rounded-3xl overflow-hidden card-soft">
        {/* Left brand panel */}
        <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-[var(--brand)] to-[#4C6BFF] text-white relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -left-10 -bottom-16 w-64 h-64 rounded-full bg-[var(--brand-2)]/30 blur-3xl" />
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2 3 6.5v10L12 21l9-4.5v-10z"/><path d="M3 6.5 12 11l9-4.5"/><path d="M12 11v10"/>
              </svg>
            </div>
            <div>
              <div className="font-display font-bold text-xl">Nafe Logistica</div>
              <div className="text-xs opacity-80">Ops · Logistics · Finance</div>
            </div>
          </div>

          <div className="relative space-y-4">
            <h1 className="font-display text-4xl font-bold leading-tight">
              Rastreabilidade.<br/>Operação.<br/>Compliance.
            </h1>
            <p className="text-sm opacity-90 leading-relaxed max-w-md">
              Plataforma SaaS para gestão de documentos, logística, evidências jurídicas e financeiro. Preparada para milhares de documentos/dia.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-4">
              {[["Documentos", "12.4k"], ["Rotas", "240"], ["Cartórios", "58"]].map(([l, v]) => (
                <div key={l} className="rounded-2xl bg-white/10 backdrop-blur p-3">
                  <div className="font-display text-2xl font-bold">{v}</div>
                  <div className="text-[10px] opacity-80">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative text-xs opacity-70">© 2026 · v1.0</div>
        </div>

        {/* Right form */}
        <div className="bg-[var(--surface)] flex flex-col">
          <div className="flex justify-end gap-2 p-5">
            <PageHelp pathname="/login" variant="compact" />
            <button onClick={() => setLang(lang === "pt" ? "en" : "pt")} className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-[var(--surface-2)] text-xs font-semibold" data-testid="login-lang-toggle">
              <Languages size={13} /> {lang.toUpperCase()}
            </button>
            <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-[var(--surface-2)] text-xs font-semibold" data-testid="login-theme-toggle">
              {theme === "dark" ? <Sun size={13} /> : <Moon size={13} />}
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center px-6 pb-8">
            <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5" data-testid="login-form">
              <div>
                <div className="text-xs text-[var(--text-2)]">{t("welcome_back")}</div>
                <h2 className="font-display text-2xl font-bold tracking-tight mt-1">{t("sign_in")}</h2>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-[var(--text-2)]">{t("email")}</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="mt-1 h-11 rounded-2xl" required />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--text-2)]">{t("password")}</label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="mt-1 h-11 rounded-2xl" required />
                </div>
              </div>

              {error && <div className="text-sm text-[var(--danger)] bg-[var(--danger-soft)] rounded-2xl p-3" data-testid="login-error">{error}</div>}

              <Button type="submit" disabled={loading} className="w-full h-11 rounded-2xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] font-semibold" data-testid="login-submit-button">
                {loading ? "..." : t("sign_in")}
              </Button>

              {demoLogins && (
                <div className="text-xs border-t border-[var(--border-c)] pt-4 space-y-1">
                  <div className="font-semibold text-[var(--text-2)] mb-2">Contas de demonstração</div>
                  {DEMO_ACCOUNTS.map(([e, p, r]) => (
                    <button key={e} type="button" onClick={() => { setEmail(e); setPassword(p); }} className="block w-full text-left hover:bg-[var(--surface-2)] rounded-lg px-2 py-1 font-mono text-[11px]" data-testid={`demo-${r.toLowerCase()}`}>
                      <span className="text-[var(--text-1)] font-semibold">{r}</span> · {e} / {p}
                    </button>
                  ))}
                </div>
              )}

              <div className="text-[11px] text-[var(--text-3)] border-t border-[var(--border-c)] pt-4 flex flex-wrap gap-x-3 gap-y-1">
                <Link to="/privacidade" className="hover:text-[var(--brand)]">Privacidade</Link>
                <Link to="/termos" className="hover:text-[var(--brand)]">Termos</Link>
                <Link to="/suporte" className="hover:text-[var(--brand)]">Suporte</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
